"use client";

import React from "react";
import { withRefProp } from "../../../utils/withRefProp";
import InputBox from "../InputBox/InputBox";
import DateInput, { type DateInputProps } from "./DateInput";
import styles from "./DateTimeInput.module.scss";
import TimeInput from "./TimeInput";
import { TIME_PRESETS, type TimePreset, type TimePresetLayout, resolvePresetTime } from "./TimeList";
import {
  type TimeOfDay,
  combineDateTime,
  deserializeDateValue,
  getLocaleDateOrder,
  parsePastedDateTime,
  parseTime,
  serializeDateValue,
  timeOf,
  timeToValue,
} from "./dateTimeFormat";

export type DateTimeInputSerialize<TValue> = {
  to: (value: Date | null) => TValue;
  from: (value: TValue | null | undefined) => Date | null;
};

export interface DateTimeInputProps<TValue = string> extends Omit<DateInputProps, "value" | "onValueChange" | "ref"> {
  value?: TValue;
  onValueChange?: (value: TValue) => void;
  /**
   * Converts between the stored value and a Date. Defaults to "yyyy-MM-dd" strings (date only) or
   * "yyyy-MM-dd HH:mm[:ss]". Pass false to store Date objects.
   */
  serialize?: DateTimeInputSerialize<TValue> | false;
  /** Render only the date control. */
  dateOnly?: boolean;
  /**
   * Time used when a date is picked before any time is set, or when the time is cleared. Accepts a
   * TimeOfDay or text like "5pm". Defaults to start of day.
   */
  defaultTime?: TimeOfDay | string;
  /**
   * "separate" renders two inputs side by side. "combined" renders them inside one field with a
   * separator; each part still opens its own picker.
   */
  variant?: "separate" | "combined";
  hour12?: boolean;
  /** Minutes between time options (default 15); null lists only the presets. */
  timeStepMinutes?: number | null;
  timePresets?: TimePreset[];
  timePresetLayout?: TimePresetLayout;
  showSeconds?: boolean;
  ref?: React.Ref<HTMLInputElement>;
}

const DateTimeInputInner = <TValue = string>({
  value,
  onValueChange,
  serialize,
  dateOnly = false,
  defaultTime: defaultTimeProp = TIME_PRESETS.startOfDay.time,
  variant = "separate",
  hour12,
  timeStepMinutes,
  timePresets,
  timePresetLayout,
  showSeconds,
  locale,
  disabled,
  error,
  fullWidth,
  className,
  endAdornment,
  onBlur,
  ref,
  ...dateProps
}: DateTimeInputProps<TValue>) => {
  const resolvedSerialize = React.useMemo<DateTimeInputSerialize<TValue> | false>(() => {
    if (serialize !== undefined) return serialize;
    return {
      to: (date) => serializeDateValue(date, dateOnly) as TValue,
      from: (stored) => deserializeDateValue(stored as string | null | undefined),
    };
  }, [dateOnly, serialize]);

  const dateValue = React.useMemo(() => {
    if (resolvedSerialize) return resolvedSerialize.from(value as TValue);
    return value instanceof Date ? value : null;
  }, [resolvedSerialize, value]);

  const emit = (next: Date | null) => {
    const serialized = (resolvedSerialize ? resolvedSerialize.to(next) : next) as TValue;
    if (Object.is(serialized, value)) return;
    if (serialized instanceof Date && value instanceof Date && serialized.getTime() === value.getTime()) return;
    onValueChange?.(serialized);
  };

  // Date and time are held separately so a time entered before a date is kept. Only the combined
  // value is emitted, once a date exists.
  const [time, setTime] = React.useState<TimeOfDay | null>(dateValue ? timeOf(dateValue) : null);
  const dateValueTime = dateValue?.getTime() ?? null;
  // Re-sync only when the stored instant changes.
  React.useEffect(() => {
    if (dateValue) setTime(timeOf(dateValue));
  }, [dateValueTime]);

  if (dateOnly) {
    return (
      <DateInput
        {...dateProps}
        ref={ref}
        className={className}
        locale={locale}
        value={dateValue}
        onValueChange={emit}
        disabled={disabled}
        error={error}
        fullWidth={fullWidth}
        endAdornment={endAdornment}
        onBlur={onBlur}
      />
    );
  }

  const defaultTime = resolvePresetTime(defaultTimeProp) ?? TIME_PRESETS.startOfDay.time;

  // Pasting a full date and time into either part (e.g. "2026-12-25T10:30:00.000Z") fills both.
  // Anything without a time falls through to the normal paste.
  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = parsePastedDateTime(event.clipboardData.getData("text"), getLocaleDateOrder(locale));
    if (!pasted?.time) return;
    event.preventDefault();
    setTime(pasted.time);
    emit(combineDateTime(pasted.date, pasted.time));
  };

  const handleDateChange = (nextDate: Date | null) => {
    const nextTime = nextDate && !time ? defaultTime : time;
    setTime(nextTime);
    emit(nextDate ? combineDateTime(nextDate, nextTime) : null);
  };

  const handleTimeChange = (nextValue: string) => {
    const parsed = parseTime(nextValue);
    const nextTime = parsed ?? (dateValue ? defaultTime : null);
    setTime(nextTime);
    if (dateValue) emit(combineDateTime(dateValue, nextTime));
  };

  const combined = variant === "combined";
  const dateInput = (
    <DateInput
      {...dateProps}
      ref={ref}
      className={className}
      locale={locale}
      value={dateValue}
      onValueChange={handleDateChange}
      onPaste={handlePaste}
      disabled={disabled}
      error={combined ? undefined : error}
      fullWidth={fullWidth}
      onBlur={onBlur}
    />
  );
  const timeInput = (
    <TimeInput
      value={time ? timeToValue(time, showSeconds) : ""}
      onValueChange={handleTimeChange}
      onPaste={handlePaste}
      locale={locale}
      hour12={hour12}
      stepMinutes={timeStepMinutes}
      presets={timePresets}
      presetLayout={timePresetLayout}
      showSeconds={showSeconds}
      disabled={disabled}
      error={combined ? undefined : error}
      endAdornment={endAdornment}
      onBlur={onBlur}
      aria-label={dateProps["aria-label"] ? `${dateProps["aria-label"]} time` : "Time"}
    />
  );

  if (combined) {
    return (
      <InputBox
        className={styles.combinedGroup}
        data-focus-ring-scope="outer"
        fullWidth={fullWidth}
        error={error}
        disabled={disabled}
        // biome-ignore lint/a11y/useSemanticElements: a fieldset brings its own border/legend styling; role="group" is equivalent
        role="group"
      >
        {dateInput}
        <span
          className={styles.combinedSeparator}
          aria-hidden
        />
        {timeInput}
      </InputBox>
    );
  }

  return (
    <div
      className={styles.dateTimeGroup}
      data-full-width={fullWidth ? "true" : "false"}
      // biome-ignore lint/a11y/useSemanticElements: a fieldset brings its own border/legend styling; role="group" is equivalent
      role="group"
    >
      {dateInput}
      {timeInput}
    </div>
  );
};

const DateTimeInput = withRefProp(DateTimeInputInner);

export default DateTimeInput;
